import {
  IDSK,
  KeysMatching,
  Maybe,
  MaybeArray,
  ObjectivesViewType,
  ObjectType,
  Replace,
  Resource,
  ResourceFile,
  S3Object,
  Scalars,
  SharedMediaType,
  Student,
  User,
  Unsaved,
  UnsavedOptionals,
  WorkspaceUserRole,
} from "@examspark/types";

import { capitalizeFirstLetter, filterMaybeArray } from "./utils";
export * from "./content/items/answerKeys";
export * from "./controller";
export * from "./editor";
export * from "./exam";
export * from "./item";
export * from "./keys";
export * from "./nces";
export * from "./samples";
export * from "./unit";

export * from "./meta";
export * from "./meta/banks";
export { bloomsMap } from "./meta/blooms";
export * from "./meta/tags";
export * from "./objective";
export * from "./search";
export * from "./system";
export * from "./tags";
export * from "./utils";
export * from "./utils/enum";
export * from "./uuids";
export * from "./workspace";

export * as zz from "./zod";

/**
 * Return a search-friendly resource type for suitable records. Not all records
 * will return a resource type.
 */
export const getObjectTypeFromRecord = (
  item: Pick<Resource, "sk" | "type"> | undefined,
): ObjectType | undefined => {
  if (item == null) return undefined;

  if (item.sk?.match(/^item#/)) {
    return "ITEM";
  }
  if (item.sk?.match(/^exam#/)) {
    return "EXAM";
  }
  if (item.sk?.match(/^task$/)) {
    return "TASK";
  }
  if (item.sk?.match(/^issue$/)) {
    return "ISSUE";
  }
  if (item.sk?.match(/^course#/)) {
    return "COURSE";
  }
  if (item.sk?.match(/^unit#/)) {
    return "UNIT";
  }
  if (item.sk?.match(/^vocabulary$/)) {
    return "VOCABULARY";
  }
  if (item.sk?.match(/^classroom$/)) {
    return "CLASSROOM";
  }
  if (item.sk?.match(/^objectives-view#/)) {
    return item.type as ObjectivesViewType;
  }
  if (item.sk?.match(/^course-verification#/)) {
    return "COURSE_VERIFICATION";
  }
  if (item.sk?.match(/^course-plan#/)) {
    return "COURSE_PLAN";
  }
  if (item.sk?.match(/^academic-calendar#/)) {
    return "ACADEMIC_CALENDAR";
  }
  if (item.sk?.match(/^course-calendar#/)) {
    return "COURSE_CALENDAR";
  }
  if (item.sk?.match(/^resource#/) && item.type) {
    return item.type as SharedMediaType;
  }
};

export const formatUserEmail = (user?: Maybe<User>) => {
  return user?.email?.toLowerCase();
};

export const formatUserName = (user?: Maybe<User>) => {
  if (!user) return;
  if (!user.firstName?.length || !user.lastName?.length) return;
  const { firstName, lastName } = user;
  return `${firstName} ${lastName}`;
};

export const formatStudentName = (user?: Maybe<Student>) => {
  if (!user) return;
  const { firstName, lastName } = user;
  return `${firstName} ${lastName}`;
};

export const formatUserMailTo = (user?: Maybe<User>) => {
  if (!user) return;
  const { email } = user;
  return `${formatUserName(user)} <${email}>`;
};

export const RESOURCE_TYPES = {
  ITEM: {
    value: "ITEM",
    label: "Question",
  },
  PASSAGE: {
    value: "PASSAGE",
    label: "Passage",
  },
  IMAGE: {
    value: "IMAGE",
    label: "Image",
  },
  AUDIO: {
    value: "AUDIO",
    label: "Audio",
  },
  VIDEO: {
    value: "VIDEO",
    label: "Video",
  },
  EXTERNAL_LINK: {
    value: "EXTERNAL_LINK",
    label: "External Link",
  },
  RUBRIC: {
    value: "RUBRIC",
    label: "Rubric",
  },
  DOCUMENT: {
    value: "DOCUMENT",
    label: "Document",
  },
  WEB_DOCUMENT: {
    value: "WEB_DOCUMENT",
    label: "Web Document",
  },
  PERFORMANCE_TASK: {
    value: "PERFORMANCE_TASK",
    label: "Performance Assessment",
  },
  SPREADSHEET: {
    value: "SPREADSHEET",
    label: "Spreadsheet",
  },
};

export const getS3FileURL = (file: S3Object | ResourceFile) => {
  return `https://${file?.bucket}.s3.amazonaws.com/${file?.key}`;
};

export const getSecureS3FileURL = (file: S3Object | ResourceFile) => {
  return `https://${file?.bucket}.s3.amazonaws.com/${file?.key}`;
};

export const getS3URL = (
  awsconfig: {
    aws_user_files_s3_bucket: string;
  },
  key: string,
) => {
  // guard against double-slash where domain and key meet.
  if (key.startsWith("/")) key = key.slice(1);

  return `https://${awsconfig.aws_user_files_s3_bucket}.s3.amazonaws.com/${key}`;
};

export const BONFIRE_USER_ROLES = {
  [WorkspaceUserRole.Standard]: {
    label: "Standard User",
    value: WorkspaceUserRole.Standard,
  },
  [WorkspaceUserRole.ContentManager]: {
    label: "Content Manager",
    value: WorkspaceUserRole.ContentManager,
  },
  [WorkspaceUserRole.Administrator]: {
    label: "Administrator",
    value: WorkspaceUserRole.Administrator,
  },
};

export const USER_ROLES = {
  [WorkspaceUserRole.Viewer]: {
    label: "Viewer",
    value: WorkspaceUserRole.Viewer,
  },
  [WorkspaceUserRole.Collector]: {
    label: "Collector",
    value: WorkspaceUserRole.Collector,
  },
  [WorkspaceUserRole.Author]: {
    label: "Author",
    value: WorkspaceUserRole.Author,
  },
  [WorkspaceUserRole.Editor]: {
    label: "Editor",
    value: WorkspaceUserRole.Editor,
  },
  [WorkspaceUserRole.Standard]: {
    label: "Standard User",
    value: WorkspaceUserRole.Standard,
  },
  [WorkspaceUserRole.ContentManager]: {
    label: "Content Manager",
    value: WorkspaceUserRole.ContentManager,
  },
  [WorkspaceUserRole.Administrator]: {
    label: "Admin (Workspace)",
    value: WorkspaceUserRole.Administrator,
  },
};

export const getDynamoDBStreamProps = (arn: string) => {
  const [, , service, region, account, streamId] = arn.split(":");
  const [, tableName] = streamId.split("/");

  return {
    service,
    region,
    account,
    tableName,
  };
};

export const printJSON = (data: any) => {
  return JSON.stringify(data, null, 2);
};

export const isDateBeforeDate = (
  date1: string,
  date2: string,
  inclusive?: boolean,
) => {
  if (inclusive) return new Date(date1) <= new Date(date2);
  return new Date(date1) < new Date(date2);
};

export const isDateAfterDate = (
  date1: string,
  date2: string,
  inclusive?: boolean,
) => {
  if (inclusive) return new Date(date1) >= new Date(date2);
  return new Date(date1) > new Date(date2);
};

/** Used to make a unique react DOM key from an `id` and `sk` */
export const reactKeyFromIDSK = (item?: Maybe<IDSK>) => {
  return `${item?.id}_${item?.sk}`;
};

export const getKeyFromEntity = <T extends IDSK>(entity?: T) => {
  if (!entity) return;

  return {
    id: entity.id,
    sk: entity.sk,
  };
};

/**
 * Calculate the ddb table name from environment vars that should be present
 * inside a lambda execution.
 */
export const getTableNameInLambdaEnvironment = (name: string): string => {
  const env = process.env.ENV ?? process.env.LOCAL_ENV;
  const api = process.env.API_EXAMSPARKAPP_GRAPHQLAPIIDOUTPUT;

  if (env == null) throw new Error("Missing ENV");
  if (api == null)
    throw new Error("Missing API_EXAMSPARKAPP_GRAPHQLAPIIDOUTPUT");

  name = capitalizeFirstLetter(name);

  return `${name}-${api}-${env}`;
};

export const findNextListItem = <T>(
  list: T[],
  currentId?: string,
  find?: (item: any) => string,
) => {
  const findIndex = list.findIndex((item: any) =>
    find ? find(item) === currentId : item.id === currentId,
  );

  if (findIndex >= list.length - 1) return list[findIndex];
  return list[findIndex + 1];
};

export const findPreviousListItem = <T>(
  list: T[],
  currentId?: string,
  find?: (item: any) => string,
) => {
  const findIndex = list.findIndex((item: any) =>
    find ? find(item) === currentId : item.id === currentId,
  );

  if (findIndex === 0) return list[findIndex];
  return list[findIndex - 1];
};

export const asContent = <
  T extends
    | Replace<Resource, { id: string | undefined }>
    | Unsaved<UnsavedOptionals & { schemaVersion: string }>,
>(
  resource: T,
) => {
  if (resource.workspaceId == null || resource.id == null) {
    return undefined;
  }
  if ("schemaVersion" in resource && resource.schemaVersion == null) {
    resource.schemaVersion = "2020-01-01";
  }
  return resource as Omit<T, "id" | "workspaceId" | "schemaVersion"> & {
    id: string;
    workspaceId: string;
    schemaVersion: string;
  };
};

type ParsedAWSJSON = Exclude<Scalars["AWSJSON"], string>;

export function parseAwsJson<T = ParsedAWSJSON>(input: Scalars["AWSJSON"]): T;
export function parseAwsJson<T = ParsedAWSJSON>(
  input: Scalars["AWSJSON"] | null | undefined,
): T | undefined;
export function parseAwsJson<T = ParsedAWSJSON>(
  input: Scalars["AWSJSON"] | null | undefined,
): T | undefined {
  if (input == null) return undefined;
  if (typeof input === "string") return JSON.parse(input);
  return input as T;
}

export function parseAwsJsonArray<T = ParsedAWSJSON>(
  input: Array<Scalars["AWSJSON"]>,
): T[];
export function parseAwsJsonArray<T = ParsedAWSJSON>(
  input: Array<Scalars["AWSJSON"]> | null | undefined,
): T[] | undefined;
export function parseAwsJsonArray<T = ParsedAWSJSON>(
  input: Array<Scalars["AWSJSON"]> | null | undefined,
): T[] | undefined {
  if (input == null) return undefined;
  return input.map((r) => parseAwsJson<T>(r));
}

type MaybeConnections<T> = MaybeArray<T> | { items: MaybeArray<T> };

export function parseConnectionArray<T>(input: MaybeConnections<T>) {
  if (input == null) return [];
  if (Array.isArray(input)) return filterMaybeArray(input);
  if ("items" in input && Array.isArray(input.items)) {
    return filterMaybeArray(input.items);
  }

  throw new Error("Provided value is not an array or a connection shape");
}

export function parseConnectionArrayInPlace<T extends object>(
  obj: T | null | undefined,
  key: KeysMatching<T, MaybeArray<any>>,
) {
  if (obj == null) return;
  obj[key] = parseConnectionArray(obj[key] as any[]) as any;
}

export const zeroGUID = `00000000-0000-0000-0000-000000000000`;
